import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowLeft, Layers, Loader2, Pin, PinOff } from 'lucide-react';
import { useCreativeStrategyStore, type PackTab } from '@/stores/creativeStrategy';
import { PackItemList } from '@/components/knowledge/creative-strategy/PackItemList';
import { PackItemDetail } from '@/components/knowledge/creative-strategy/PackItemDetail';
import { PackGapsPanel } from '@/components/knowledge/creative-strategy/PackGapsPanel';
import { PackCitationsPanel } from '@/components/knowledge/creative-strategy/PackCitationsPanel';
import { PackProvenancePanel } from '@/components/knowledge/creative-strategy/PackProvenancePanel';

const TABS: Array<{ id: PackTab; label: string; countKey?: string }> = [
  { id: 'concepts', label: 'Buyer Concepts', countKey: 'market_concept' },
  { id: 'strategies', label: 'Strategies', countKey: 'creative_strategy' },
  { id: 'templates', label: 'Templates', countKey: 'creative_pattern' },
  { id: 'gaps', label: 'Gaps & Caveats' },
  { id: 'citations', label: 'Citations' },
  { id: 'provenance', label: 'Provenance' },
];

export function CreativeStrategyPackPage() {
  const { clientUid = '', itemId } = useParams<{ clientUid: string; itemId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const viewVersion = searchParams.get('version');

  const { pack, packLoading, saving, error, tab, setTab, fetchPack, fetchCitations, selectItem, pin } =
    useCreativeStrategyStore();
  const [confirmPin, setConfirmPin] = useState(false);

  useEffect(() => {
    if (clientUid) {
      fetchPack(clientUid, viewVersion);
      fetchCitations(clientUid);
    }
  }, [clientUid, viewVersion, fetchPack, fetchCitations]);

  // Deep link: /creative-strategy/:client/:itemId lands on the item's tab.
  useEffect(() => {
    if (!itemId || !pack) return;
    const item = pack.items.find((i) => i.resolved_id === itemId);
    if (!item) return;
    const t: PackTab =
      item.entity_class === 'market_concept' ? 'concepts' : item.entity_class === 'creative_strategy' ? 'strategies' : 'templates';
    if (t !== tab) setTab(t);
    selectItem(itemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, pack]);

  const onSelectItem = (id: string | null) => {
    selectItem(id);
    const base = `/knowledge/creative-strategy/dockets/${encodeURIComponent(clientUid)}`;
    navigate(id ? `${base}/${encodeURIComponent(id)}${viewVersion ? `?version=${viewVersion}` : ''}` : base, {
      replace: true,
    });
  };

  const onVersionChange = (v: string) => {
    const served = pack?.pinned_version ?? pack?.latest_version;
    if (!v || v === served) setSearchParams({});
    else setSearchParams({ version: v });
    selectItem(null);
  };

  const isPinnedHere = !!pack && !!pack.pinned_version && pack.pinned_version === pack.version;

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="px-6 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/knowledge/creative-strategy/dockets" className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
              <ArrowLeft size={14} /> Dockets
            </Link>
            <div className="flex items-center gap-2 min-w-0">
              <Layers size={16} className="text-brand-teal" />
              <span className="text-sm font-semibold text-navy-900 truncate">{clientUid}</span>
              {pack && (
                <span
                  className={clsx(
                    'text-[11px] font-medium rounded-full px-2 py-0.5 border',
                    pack.enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200',
                  )}
                >
                  {pack.enabled ? 'serving' : 'flag off'}
                </span>
              )}
              {pack?.scope_detail?.paths?.[0] && (
                <span className="text-xs text-slate-500 truncate hidden md:inline">{pack.scope_detail.paths[0]}</span>
              )}
            </div>
          </div>

          {pack && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <select
                value={pack.version ?? ''}
                onChange={(e) => onVersionChange(e.target.value)}
                className="bg-slate-50 text-xs font-mono text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none"
              >
                {pack.versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    {v.version}
                    {v.is_latest ? ' · latest' : ''}
                    {v.version === pack.pinned_version ? ' · pinned' : ''}
                  </option>
                ))}
              </select>

              {isPinnedHere ? (
                <button
                  onClick={() => pin(clientUid, null)}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  title="Stop pinning; serve latest.txt again"
                >
                  <PinOff size={12} /> Unpin, follow latest
                </button>
              ) : confirmPin ? (
                <span className="inline-flex items-center gap-1.5 text-xs">
                  <span className="text-slate-600">Serve {pack.version} to briefs and angles?</span>
                  <button
                    onClick={() => {
                      setConfirmPin(false);
                      pin(clientUid, pack.version);
                      setSearchParams({});
                    }}
                    disabled={saving}
                    className="px-2 py-1 rounded-md bg-navy-900 text-white hover:bg-navy-800 disabled:opacity-50"
                  >
                    Pin
                  </button>
                  <button onClick={() => setConfirmPin(false)} className="px-2 py-1 rounded-md border border-slate-200 text-slate-600">
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => setConfirmPin(true)}
                  disabled={saving || (pack.is_served_version && !pack.pinned_version && pack.version === pack.latest_version && false)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  title="Serve this exact version regardless of later publishes"
                >
                  <Pin size={12} /> Pin this version
                </button>
              )}
            </div>
          )}
        </div>

        {pack && !pack.is_served_version && (
          <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
            Viewing {pack.version}. The platform is serving {pack.pinned_version ?? pack.latest_version}
            {pack.pinned_version ? ' (pinned)' : ' (latest)'}. Hide/unhide applies to the served pack.
          </div>
        )}

        <div className="flex items-center gap-1 mt-3">
          {TABS.map((t) => {
            const n = t.countKey ? pack?.counts?.[t.countKey] : t.id === 'gaps' ? pack?.gaps.length : undefined;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={clsx(
                  'px-3 py-1.5 text-sm rounded-md transition-colors',
                  tab === t.id ? 'bg-navy-900 text-white' : 'text-slate-600 hover:bg-slate-100',
                )}
              >
                {t.label}
                {typeof n === 'number' && (
                  <span className={clsx('ml-1.5 text-[10px] tabular-nums', tab === t.id ? 'text-slate-300' : 'text-slate-400')}>{n}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {error && <div className="mx-6 mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      {packLoading || !pack ? (
        <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
          {packLoading ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" /> Loading docket…
            </>
          ) : (
            'No docket evidence published for this client'
          )}
        </div>
      ) : tab === 'gaps' ? (
        <div className="flex-1 overflow-auto p-6">
          <PackGapsPanel pack={pack} />
        </div>
      ) : tab === 'citations' ? (
        <div className="flex-1 overflow-auto p-6">
          <PackCitationsPanel clientUid={clientUid} onOpenItem={onSelectItem} />
        </div>
      ) : tab === 'provenance' ? (
        <div className="flex-1 overflow-auto p-6">
          <PackProvenancePanel pack={pack} />
        </div>
      ) : (
        <div className="flex flex-1 min-h-0">
          <div className="w-96 flex-shrink-0">
            <PackItemList onSelect={onSelectItem} />
          </div>
          <div className="flex-1 min-w-0 bg-white">
            <PackItemDetail clientUid={clientUid} />
          </div>
        </div>
      )}
    </div>
  );
}
